import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ConstructionCostService } from '../services/construction-cost.service';
import type { CostFilters } from '../types/construction-cost.types';
import { useState } from 'react';
import { toast } from 'sonner';

export function useConstructionCosts(companyId: string, initialFilters: CostFilters = {}) {
  const [filters, setFilters] = useState<CostFilters>({
    status: 'all',
    type: 'all',
    ...initialFilters
  });

  const query = useQuery({
    queryKey: ['construction-costs', companyId, filters],
    queryFn: () => ConstructionCostService.getCosts(companyId, filters),
    enabled: !!companyId,
  });

  const summaryQuery = useQuery({
    queryKey: ['construction-cost-summary', companyId, filters.projectId],
    queryFn: () => ConstructionCostService.getSummary(companyId, filters.projectId),
    enabled: !!companyId,
  });

  return {
    costs: query.data?.data || [],
    total: query.data?.total || 0,
    summary: summaryQuery.data,
    isLoading: query.isLoading || summaryQuery.isLoading,
    filters,
    setFilters,
  };
}

export function useConstructionCostDetail(id?: string) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['construction-costs', 'detail', id],
    queryFn: () => ConstructionCostService.getCostDetail(id!),
    enabled: !!id,
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ status, note }: { status: string; note?: string }) => 
      ConstructionCostService.updateStatus(id!, status, note),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['construction-costs'] });
      queryClient.invalidateQueries({ queryKey: ['construction-costs', 'detail', id] });
      toast.success('Cập nhật trạng thái chi phí thành công');
    },
  });

  return {
    cost: query.data,
    isLoading: query.isLoading,
    updateStatus: updateStatusMutation.mutateAsync,
    isUpdating: updateStatusMutation.isPending,
  };
}
